import { useState, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import { ClipboardCheck, ExternalLink, RefreshCw, ShieldCheck, AlertCircle, FlaskConical, CheckCircle2, XCircle, Brain } from 'lucide-react'
import { format } from 'date-fns'
import { de } from 'date-fns/locale'
import type { ColumnDef, Row } from '@tanstack/react-table'
import { apiClient } from '@/lib/api.ts'
import { usePermission } from '@/hooks/usePermission.ts'
import { useAuthStore } from '@/stores/auth.ts'
import {
  PageHeader,
  Button,
  Card,
  LoadingState,
  ErrorState,
  EmptyState,
  DataTable,
  StatusBadge,
  Modal,
  Textarea,
  ConfirmDialog,
  useToast,
  Tooltip,
} from '@/components/ui'

// ─── Types ───────────────────────────────────────────────────────────────────

interface DryRunPreviewTicket {
  tenantId: string
  tenantCode: string
  companyId: number | null
  companyName: string
  tanssTicketId: number
  ticketNumber: string
  ticketTitle: string
  serviceCount: number
  wouldClear: boolean
  blockingRules: string[]
}

interface RuleResult {
  ruleKey: string
  passed: boolean
  blocking: boolean
  message: string
  actualValue?: unknown
  expectedValue?: unknown
  affectedServiceIds?: number[]
}

interface ClearedTicketRow {
  id: string
  run_id: string
  tenant_id: string
  tanss_ticket_id: number
  ticket_number: string
  clearance_status_after: string
  affected_service_ids: string | number[] | null
  created_at: string
}

interface ProblemTicketRow {
  id: string
  run_id: string
  tenant_id: string
  tanss_ticket_id: number
  ticket_number: string
  clearance_status_before: string | null
  clearance_status_after: string | null
  rule_results: string | RuleResult[] | null
  ai_result: string | null
  error_messages: string | string[] | null
  created_at: string
}

interface ClearanceRun {
  id: string
  triggerType: string
  status: string
  startedAt: string
  endedAt: string | null
  ticketsChecked: number
  servicesCleared: number
  ticketsBlocked: number
}

interface OverrideTarget {
  ticketId: number
  ticketNumber: string
  serviceIds: number[]
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function parseJson<T>(value: unknown): T | null {
  if (value === null || value === undefined) return null
  if (typeof value === 'string') {
    try { return JSON.parse(value) as T } catch { return null }
  }
  return value as T
}

function tanssTicketUrl(ticketId: number): string {
  return `https://tanss.implec.de/index.php?section=bug&sub=view&bugID=${ticketId}`
}

function formatTs(ts: string | null | undefined): string {
  if (!ts) return '—'
  try {
    return format(new Date(ts), 'dd.MM.yyyy HH:mm', { locale: de })
  } catch {
    return ts
  }
}

function getServiceIds(row: ProblemTicketRow): number[] {
  const rules = parseJson<RuleResult[]>(row.rule_results) ?? []
  const ids = new Set<number>()
  for (const r of rules) {
    if (r.blocking && r.affectedServiceIds) {
      for (const id of r.affectedServiceIds) ids.add(id)
    }
  }
  return Array.from(ids)
}

function errMsg(e: unknown): string {
  if (isAxiosError(e)) {
    const d = e.response?.data as { error?: string; message?: string } | undefined
    return d?.error ?? d?.message ?? e.message
  }
  if (e instanceof Error) return e.message
  return String(e)
}

// ─── AI result types and helpers ──────────────────────────────────────────────

interface AiCheckApiResult {
  tenantId: string
  ticketId: number
  result: 'BESTANDEN' | 'BEANSTANDET'
  confidence: number
  reason: string
  checkedAt: string | Date
  promptVersion: number
  modelIdentifier: string | null
  fromCache: boolean
  isError?: boolean
  durationMs?: number
}

function parseRunAiResult(raw: string | null): { result: 'BESTANDEN' | 'BEANSTANDET'; confidence: number; reason: string; isError: boolean } | null {
  if (!raw) return null
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw
    if (!parsed || typeof parsed !== 'object') return null
    if (!parsed.result) return null
    const isError = !parsed.modelId || parsed.modelId === ''
    return {
      result: parsed.result as 'BESTANDEN' | 'BEANSTANDET',
      confidence: Number(parsed.confidence ?? 0),
      reason: String(parsed.reason ?? ''),
      isError,
    }
  } catch {
    return null
  }
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function TanssLink({ ticketId, ticketNumber }: { ticketId: number; ticketNumber: string }) {
  return (
    <a
      href={tanssTicketUrl(ticketId)}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 text-brand-600 hover:text-brand-800 hover:underline text-sm"
    >
      {ticketNumber}
      <ExternalLink className="h-3 w-3 flex-shrink-0" />
    </a>
  )
}

function RuleResultsList({ ruleResults }: { ruleResults: RuleResult[] }) {
  const blocking = ruleResults.filter((r) => !r.passed && r.blocking)
  const warnings = ruleResults.filter((r) => !r.passed && !r.blocking)

  if (blocking.length === 0 && warnings.length === 0) {
    return <p className="text-sm text-gray-500 italic">Keine Regeldetails verfügbar.</p>
  }

  return (
    <div className="space-y-2">
      {blocking.map((r, i) => (
        <div key={i} className="flex items-start gap-2">
          <AlertCircle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
          <div>
            <span className="text-sm font-medium text-red-700">{r.ruleKey}</span>
            <p className="text-sm text-gray-700">{r.message}</p>
            {r.affectedServiceIds && r.affectedServiceIds.length > 0 && (
              <p className="text-xs text-gray-500 mt-0.5">
                Betroffene Leistungen: {r.affectedServiceIds.join(', ')}
              </p>
            )}
          </div>
        </div>
      ))}
      {warnings.map((r, i) => (
        <div key={i} className="flex items-start gap-2">
          <AlertCircle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
          <div>
            <span className="text-sm font-medium text-amber-700">{r.ruleKey}</span>
            <p className="text-sm text-gray-700">{r.message}</p>
          </div>
        </div>
      ))}
    </div>
  )
}

// ─── Override Modal ───────────────────────────────────────────────────────────

interface OverrideModalProps {
  target: OverrideTarget | null
  onClose: () => void
  onSubmit: (ticketId: number, serviceIds: number[], reason: string) => void
  isLoading: boolean
}

function OverrideModal({ target, onClose, onSubmit, isLoading }: OverrideModalProps) {
  const [reason, setReason] = useState('')
  const [showConfirm, setShowConfirm] = useState(false)

  const handleConfirmClick = useCallback(() => {
    if (reason.trim().length < 5) return
    setShowConfirm(true)
  }, [reason])

  const handleConfirmed = useCallback(() => {
    if (!target) return
    onSubmit(target.ticketId, target.serviceIds, reason.trim())
  }, [target, reason, onSubmit])

  const handleClose = useCallback(() => {
    setReason('')
    setShowConfirm(false)
    onClose()
  }, [onClose])

  if (!target) return null

  const reasonTooShort = reason.length > 0 && reason.trim().length < 5

  return (
    <>
      <Modal
        open={!showConfirm}
        title={`Ticket ${target.ticketNumber} übersteuern`}
        onClose={handleClose}
        size="md"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={handleClose} disabled={isLoading}>
              Abbrechen
            </Button>
            <Button
              variant="danger"
              onClick={handleConfirmClick}
              disabled={reason.trim().length < 5 || isLoading}
            >
              Weiter
            </Button>
          </div>
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-gray-600">
            Die Freigabe wird manuell erzwungen.
            {target.serviceIds.length > 0 && (
              <> {target.serviceIds.length} Leistung(en) werden freigegeben.</>
            )}
          </p>
          <Textarea
            label="Begründung *"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Mindestens 5 Zeichen …"
            rows={4}
            error={reasonTooShort ? 'Mindestens 5 Zeichen erforderlich.' : undefined}
          />
        </div>
      </Modal>

      <ConfirmDialog
        open={showConfirm}
        title="Übersteuern bestätigen?"
        message={`Ticket ${target.ticketNumber} wird manuell freigegeben. Begründung: "${reason.trim()}"`}
        confirmLabel="Jetzt übersteuern"
        variant="danger"
        loading={isLoading}
        onConfirm={handleConfirmed}
        onCancel={() => setShowConfirm(false)}
      />
    </>
  )
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export function DispoTicketpruefungPage() {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const { allowed: canOverride } = usePermission('dispo.freigabe', 'override')
  const currentUser = useAuthStore((s) => s.user)
  const isAdmin = currentUser?.role === 'admin'

  const [overrideTarget, setOverrideTarget] = useState<OverrideTarget | null>(null)
  const [rerunningId, setRerunningId] = useState<number | null>(null)

  // Per-ticket AI check state (manual triggers)
  const [aiCheckLoading, setAiCheckLoading] = useState<Set<number>>(new Set())
  const [manualAiResults, setManualAiResults] = useState<Map<number, AiCheckApiResult>>(new Map())

  const triggerAiCheck = useCallback(async (ticketId: number, tenantId: string) => {
    setAiCheckLoading((prev) => new Set([...prev, ticketId]))
    try {
      const res = await apiClient.post<AiCheckApiResult>(
        `/dispo/clearance/tickets/${ticketId}/ai-check`,
        {},
        { headers: { 'X-Tenant-Id': tenantId } },
      )
      setManualAiResults((prev) => new Map([...prev, [ticketId, res.data]]))
      if (res.data.isError) {
        toast({ title: 'KI-Prüfung: Technischer Fehler', description: res.data.reason, tone: 'error' })
      } else {
        toast({
          title: res.data.result === 'BESTANDEN' ? 'KI: Bestanden' : 'KI: Beanstandet',
          description: `Confidence: ${res.data.confidence}%`,
          tone: res.data.result === 'BESTANDEN' ? 'success' : 'warning',
        })
      }
    } catch (err) {
      toast({ title: 'KI-Prüfung fehlgeschlagen', description: errMsg(err), tone: 'error' })
    } finally {
      setAiCheckLoading((prev) => {
        const next = new Set(prev)
        next.delete(ticketId)
        return next
      })
    }
  }, [toast])
  const [dryRunPreview, setDryRunPreview] = useState<DryRunPreviewTicket[] | null>(null)

  // ── Queries ───────────────────────────────────────────────────────────────

  const runsQuery = useQuery<{ runs: ClearanceRun[] }>({
    queryKey: ['clearance-runs'],
    queryFn: () => apiClient.get('/dispo/pruefung/runs?limit=1').then((r) => r.data),
    staleTime: 30_000,
  })

  const clearedQuery = useQuery<{ tickets: ClearedTicketRow[] }>({
    queryKey: ['clearance-cleared-this-week'],
    queryFn: () => apiClient.get('/dispo/pruefung/cleared-this-week').then((r) => r.data),
    staleTime: 60_000,
  })

  const problemsQuery = useQuery<{ runId: string | null; tickets: ProblemTicketRow[] }>({
    queryKey: ['clearance-problems'],
    queryFn: () => apiClient.get('/dispo/pruefung/problems').then((r) => r.data),
    staleTime: 60_000,
  })

  const lastRun = runsQuery.data?.runs[0] ?? null

  // ── Mutations ─────────────────────────────────────────────────────────────

  const runMutation = useMutation({
    mutationFn: () => apiClient.post('/dispo/pruefung/run', {}),
    onSuccess: (res) => {
      const stats = (res.data?.stats ?? {}) as {
        ticketsChecked?: number
        servicesCleared?: number
        ticketsBlocked?: number
        isDryRun?: boolean
        dryRunPreview?: DryRunPreviewTicket[]
      }
      if (stats.isDryRun) {
        setDryRunPreview(stats.dryRunPreview ?? [])
        toast({
          title: 'Dev-Modus: Vorschau erstellt',
          description: `${stats.ticketsChecked ?? 0} Tickets geprüft · ${stats.servicesCleared ?? 0} würden freigegeben · ${stats.ticketsBlocked ?? 0} blockiert (keine Änderungen in TANSS)`,
          tone: 'info',
        })
      } else {
        setDryRunPreview(null)
        toast({
          title: 'Prüflauf abgeschlossen',
          description: `${stats.ticketsChecked ?? 0} Tickets geprüft · ${stats.servicesCleared ?? 0} freigegeben · ${stats.ticketsBlocked ?? 0} blockiert`,
          tone: 'success',
        })
        queryClient.invalidateQueries({ queryKey: ['clearance-runs'] })
        queryClient.invalidateQueries({ queryKey: ['clearance-cleared-this-week'] })
        queryClient.invalidateQueries({ queryKey: ['clearance-problems'] })
      }
    },
    onError: (err) => {
      toast({ title: 'Prüflauf fehlgeschlagen', description: errMsg(err), tone: 'error' })
    },
  })

  const rerunMutation = useMutation({
    mutationFn: (ticketId: number) =>
      apiClient.post(`/dispo/pruefung/ticket/${ticketId}/rerun`, {}),
    onSuccess: () => {
      toast({ title: 'Erneute Prüfung abgeschlossen', tone: 'success' })
      queryClient.invalidateQueries({ queryKey: ['clearance-problems'] })
      queryClient.invalidateQueries({ queryKey: ['clearance-runs'] })
    },
    onError: (err) => {
      toast({ title: 'Erneute Prüfung fehlgeschlagen', description: errMsg(err), tone: 'error' })
    },
    onSettled: () => setRerunningId(null),
  })

  const overrideMutation = useMutation({
    mutationFn: ({ ticketId, serviceIds, reason }: { ticketId: number; serviceIds: number[]; reason: string }) =>
      apiClient.post(`/dispo/pruefung/ticket/${ticketId}/override`, { reason, serviceIds }),
    onSuccess: (res) => {
      const data = res.data as { servicesCleared?: number; errors?: string[] } | undefined
      if (data?.errors?.length) {
        toast({
          title: 'Übersteuerung teilweise fehlgeschlagen',
          description: data.errors.join('; '),
          tone: 'warning',
        })
      } else {
        toast({
          title: 'Übersteuerung erfolgreich',
          description: `${data?.servicesCleared ?? 0} Leistung(en) freigegeben.`,
          tone: 'success',
        })
      }
      setOverrideTarget(null)
      queryClient.invalidateQueries({ queryKey: ['clearance-problems'] })
      queryClient.invalidateQueries({ queryKey: ['clearance-runs'] })
      queryClient.invalidateQueries({ queryKey: ['clearance-cleared-this-week'] })
    },
    onError: (err) => {
      toast({ title: 'Übersteuerung fehlgeschlagen', description: errMsg(err), tone: 'error' })
    },
  })

  // ── Column definitions ────────────────────────────────────────────────────

  const clearedColumns: ColumnDef<ClearedTicketRow>[] = [
    {
      accessorKey: 'ticket_number',
      header: 'Ticket',
      cell: ({ row }) => (
        <TanssLink
          ticketId={row.original.tanss_ticket_id}
          ticketNumber={row.original.ticket_number}
        />
      ),
    },
    {
      id: 'service_count',
      header: 'Leistungen',
      cell: ({ row }) => {
        const ids = parseJson<number[]>(row.original.affected_service_ids) ?? []
        return (
          <span className="text-sm text-gray-700">
            {ids.length > 0 ? `${ids.length} Leistung(en)` : '—'}
          </span>
        )
      },
    },
    {
      accessorKey: 'clearance_status_after',
      header: 'Status',
      cell: ({ row }) => <StatusBadge status={row.original.clearance_status_after} />,
    },
    {
      accessorKey: 'created_at',
      header: 'Freigabe-Zeitpunkt',
      cell: ({ row }) => (
        <span className="text-sm text-gray-700">{formatTs(row.original.created_at)}</span>
      ),
    },
  ]

  const problemColumns: ColumnDef<ProblemTicketRow>[] = [
    {
      accessorKey: 'ticket_number',
      header: 'Ticket',
      cell: ({ row }) => (
        <TanssLink
          ticketId={row.original.tanss_ticket_id}
          ticketNumber={row.original.ticket_number}
        />
      ),
    },
    {
      accessorKey: 'clearance_status_after',
      header: 'Status',
      cell: ({ row }) => (
        <StatusBadge status={row.original.clearance_status_after ?? 'blocked'} />
      ),
    },
    {
      id: 'error_count',
      header: 'Fehler',
      cell: ({ row }) => {
        const rules = parseJson<RuleResult[]>(row.original.rule_results) ?? []
        const blockingCount = rules.filter((r) => !r.passed && r.blocking).length
        const msgs = parseJson<string[]>(row.original.error_messages) ?? []
        const total = blockingCount + msgs.length
        return (
          <span className={`text-sm font-medium ${total > 0 ? 'text-red-700' : 'text-gray-500'}`}>
            {total > 0 ? `${total} Fehler` : '—'}
          </span>
        )
      },
    },
    {
      accessorKey: 'created_at',
      header: 'Letzter Prüflauf',
      cell: ({ row }) => (
        <span className="text-sm text-gray-700">{formatTs(row.original.created_at)}</span>
      ),
    },
    ...(isAdmin ? [{
      id: 'ai_check',
      header: 'KI-Prüfung',
      enableSorting: false,
      cell: ({ row }: { row: Row<ProblemTicketRow> }) => {
        const tid = row.original.tanss_ticket_id
        const tenantId = row.original.tenant_id
        const isLoading = aiCheckLoading.has(tid)

        // Manual result takes precedence over run-stored result
        const manualResult = manualAiResults.get(tid)
        const runResult = parseRunAiResult(row.original.ai_result)
        const displayResult = manualResult
          ? {
              result: manualResult.result,
              confidence: manualResult.confidence,
              reason: manualResult.reason,
              checkedAt: String(manualResult.checkedAt),
              isError: manualResult.isError ?? false,
            }
          : runResult
            ? {
                result: runResult.result,
                confidence: runResult.confidence,
                reason: runResult.reason,
                checkedAt: row.original.created_at,
                isError: runResult.isError,
              }
            : null

        if (!displayResult || displayResult.isError) {
          return (
            <div className="flex items-center gap-2">
              {displayResult?.isError && (
                <span className="text-xs text-red-600 font-medium">Technischer Fehler</span>
              )}
              <Button
                variant="ghost"
                size="sm"
                disabled={isLoading}
                onClick={() => { void triggerAiCheck(tid, tenantId) }}
              >
                <Brain className={`h-3.5 w-3.5 mr-1 ${isLoading ? 'animate-pulse' : ''}`} />
                {isLoading ? 'Prüft …' : 'KI prüfen'}
              </Button>
            </div>
          )
        }

        const label = displayResult.result === 'BESTANDEN'
          ? `KI: Bestanden · ${displayResult.confidence}%`
          : `KI: Beanstandet · ${displayResult.confidence}%`
        const statusKey = displayResult.result === 'BESTANDEN' ? 'ai_bestanden' : 'ai_beanstandet'

        return (
          <Tooltip
            content={
              <div className="max-w-xs space-y-1">
                <p className="text-xs font-medium">{label}</p>
                <p className="text-xs text-gray-300 whitespace-pre-line">{displayResult.reason}</p>
                <p className="text-xs text-gray-400">{formatTs(displayResult.checkedAt)}</p>
              </div>
            }
            side="left"
            delayDuration={150}
          >
            <span className="cursor-help inline-flex">
              <StatusBadge status={statusKey} label={label} />
            </span>
          </Tooltip>
        )
      },
    } as ColumnDef<ProblemTicketRow>] : []),
    {
      id: 'actions',
      header: '',
      enableSorting: false,
      cell: ({ row }) => {
        const tid = row.original.tanss_ticket_id
        const serviceIds = getServiceIds(row.original)
        const isThisRerunning = rerunMutation.isPending && rerunningId === tid

        return (
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setRerunningId(tid)
                rerunMutation.mutate(tid)
              }}
              disabled={isThisRerunning || rerunMutation.isPending}
            >
              <RefreshCw className={`h-3.5 w-3.5 mr-1 ${isThisRerunning ? 'animate-spin' : ''}`} />
              Erneut prüfen
            </Button>
            {canOverride && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() =>
                  setOverrideTarget({
                    ticketId: tid,
                    ticketNumber: row.original.ticket_number,
                    serviceIds,
                  })
                }
                disabled={overrideMutation.isPending}
              >
                <ShieldCheck className="h-3.5 w-3.5 mr-1" />
                Übersteuern
              </Button>
            )}
          </div>
        )
      },
    },
  ]

  // ── Dry-run columns ───────────────────────────────────────────────────────

  const dryRunColumns: ColumnDef<DryRunPreviewTicket>[] = [
    {
      accessorKey: 'tenantCode',
      header: 'Mandant',
      cell: ({ row }) => (
        <span className="text-sm font-mono text-gray-700">{row.original.tenantCode}</span>
      ),
    },
    {
      accessorKey: 'companyName',
      header: 'Kunde',
      cell: ({ row }) => (
        <span className="text-sm text-gray-900">{row.original.companyName}</span>
      ),
    },
    {
      accessorKey: 'ticketNumber',
      header: 'Ticket',
      cell: ({ row }) => (
        <TanssLink ticketId={row.original.tanssTicketId} ticketNumber={row.original.ticketNumber} />
      ),
    },
    {
      accessorKey: 'ticketTitle',
      header: 'Titel',
      cell: ({ row }) => (
        <span className="text-sm text-gray-700 max-w-xs truncate block">{row.original.ticketTitle}</span>
      ),
    },
    {
      accessorKey: 'serviceCount',
      header: 'Leistungen',
      cell: ({ row }) => (
        <span className="text-sm text-gray-700">{row.original.serviceCount}</span>
      ),
    },
    {
      accessorKey: 'wouldClear',
      header: 'Würde freigegeben?',
      cell: ({ row }) => row.original.wouldClear ? (
        <span className="inline-flex items-center gap-1 text-green-700 text-sm font-medium">
          <CheckCircle2 className="h-4 w-4" /> Ja
        </span>
      ) : (
        <span className="inline-flex items-center gap-1 text-red-700 text-sm font-medium">
          <XCircle className="h-4 w-4" /> Nein
        </span>
      ),
    },
    {
      id: 'blockingRules',
      header: 'Blockierende Regeln',
      cell: ({ row }) => row.original.blockingRules.length > 0 ? (
        <span className="text-xs text-red-700 font-mono">{row.original.blockingRules.join(', ')}</span>
      ) : (
        <span className="text-xs text-gray-400">—</span>
      ),
    },
  ]

  // ── Header ────────────────────────────────────────────────────────────────

  const headerActions = (
    <div className="flex items-center gap-3">
      {lastRun && (
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <StatusBadge status={lastRun.status} />
          <span>Letzter Lauf: {formatTs(lastRun.endedAt ?? lastRun.startedAt)}</span>
        </div>
      )}
      <Button
        variant="primary"
        size="sm"
        onClick={() => runMutation.mutate()}
        disabled={runMutation.isPending}
      >
        <ClipboardCheck className={`h-4 w-4 mr-1.5 ${runMutation.isPending ? 'animate-pulse' : ''}`} />
        {runMutation.isPending ? 'Läuft …' : 'Jetzt prüfen'}
      </Button>
    </div>
  )

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      <PageHeader title="Ticketprüfung" actions={headerActions} />

      {runMutation.isPending && (
        <LoadingState label="Prüflauf läuft – alle Tickets werden geprüft, bitte warten …" />
      )}

      {/* Dev-Modus Dry-Run Vorschau */}
      {dryRunPreview !== null && (
        <Card>
          <Card.Header>
            <div className="flex items-center gap-2">
              <FlaskConical className="h-5 w-5 text-amber-600" />
              <div>
                <h2 className="text-h3 font-semibold text-amber-900">Dev-Modus: Freigabe-Vorschau</h2>
                <p className="text-sm text-amber-700 mt-0.5">
                  Kein Schreibzugriff auf TANSS — dies zeigt nur, was bei einem echten Lauf passieren würde.
                  {dryRunPreview.length === 0 && ' Keine Tickets gefunden.'}
                </p>
              </div>
              <Button variant="ghost" size="sm" className="ml-auto" onClick={() => setDryRunPreview(null)}>
                Schließen
              </Button>
            </div>
          </Card.Header>
          <Card.Body>
            <DataTable<DryRunPreviewTicket>
              columns={dryRunColumns}
              data={dryRunPreview}
              getRowId={(row) => `${row.tenantCode}-${row.tanssTicketId}`}
              searchable
              searchPlaceholder="Mandant, Kunde oder Ticket suchen …"
              emptyState={
                <EmptyState
                  icon={<FlaskConical className="h-8 w-8" />}
                  title="Keine Tickets gefunden"
                  description="Der Prüflauf hat keine relevanten Tickets gefunden."
                />
              }
            />
          </Card.Body>
        </Card>
      )}

      {/* Abschnitt 1: Diese Woche freigegeben */}
      <Card>
        <Card.Header>
          <div>
            <h2 className="text-h3 font-semibold">Letzte 7 Prüfläufe – freigegeben</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              Tickets die in den letzten 7 Prüfläufen freigegeben wurden
            </p>
          </div>
        </Card.Header>
        <Card.Body>
          {clearedQuery.isError ? (
            <ErrorState
              title="Fehler beim Laden"
              description={errMsg(clearedQuery.error)}
              details={String(clearedQuery.error)}
              onRetry={() => { void clearedQuery.refetch() }}
            />
          ) : (
            <DataTable<ClearedTicketRow>
              columns={clearedColumns}
              data={clearedQuery.data?.tickets ?? []}
              loading={clearedQuery.isLoading}
              getRowId={(row) => row.id}
              searchable
              searchPlaceholder="Ticket suchen …"
              emptyState={
                <EmptyState
                  icon={<ClipboardCheck className="h-8 w-8" />}
                  title="Keine Tickets diese Woche freigegeben"
                  description="Sobald der Prüflauf Tickets freigibt, erscheinen sie hier."
                />
              }
            />
          )}
        </Card.Body>
      </Card>

      {/* Abschnitt 2: Probleme */}
      <Card>
        <Card.Header>
          <div>
            <h2 className="text-h3 font-semibold">Probleme</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              Tickets aus dem letzten Prüflauf, die nicht automatisch freigegeben wurden
            </p>
          </div>
        </Card.Header>
        <Card.Body>
          {problemsQuery.isError ? (
            <ErrorState
              title="Fehler beim Laden"
              description={errMsg(problemsQuery.error)}
              details={String(problemsQuery.error)}
              onRetry={() => { void problemsQuery.refetch() }}
            />
          ) : (
            <DataTable<ProblemTicketRow>
              columns={problemColumns}
              data={problemsQuery.data?.tickets ?? []}
              loading={problemsQuery.isLoading}
              getRowId={(row) => row.id}
              searchable
              searchPlaceholder="Ticket suchen …"
              getRowCanExpand={() => true}
              renderExpandedRow={(row: Row<ProblemTicketRow>) => {
                const rules = parseJson<RuleResult[]>(row.original.rule_results) ?? []
                const errorMsgs = parseJson<string[]>(row.original.error_messages) ?? []
                return (
                  <div className="px-4 py-3 bg-gray-50 border-t border-gray-100 space-y-3">
                    <h4 className="text-sm font-medium text-gray-700">Regeldetails</h4>
                    <RuleResultsList ruleResults={rules} />
                    {errorMsgs.length > 0 && (
                      <div className="mt-2 space-y-1">
                        <p className="text-xs font-medium text-red-700">Technische Fehler:</p>
                        {errorMsgs.map((msg, i) => (
                          <p
                            key={i}
                            className="text-xs text-red-600 font-mono bg-red-50 px-2 py-1 rounded"
                          >
                            {msg}
                          </p>
                        ))}
                      </div>
                    )}
                  </div>
                )
              }}
              emptyState={
                <EmptyState
                  icon={<ClipboardCheck className="h-8 w-8" />}
                  title="Keine Probleme gefunden"
                  description="Der letzte Prüflauf hat keine blockierten Tickets zurückgegeben."
                />
              }
            />
          )}
        </Card.Body>
      </Card>

      {/* Override Modal */}
      <OverrideModal
        target={overrideTarget}
        onClose={() => setOverrideTarget(null)}
        onSubmit={(ticketId, serviceIds, reason) =>
          overrideMutation.mutate({ ticketId, serviceIds, reason })
        }
        isLoading={overrideMutation.isPending}
      />
    </div>
  )
}
